import { type Instance, instanceSchema } from '@matter_extractor/shared';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';

/**
 * Which server this is, read once by the shell and held for every screen
 * (docs/specs/vitrine.md section 2, issue #192).
 *
 * ## Two doors, and why they are two
 *
 * `useInstance()` is the QUERY on `GET /api/instance` and the shell is its only
 * caller: it waits for the answer before drawing the admin, so a vitrine never
 * flashes the atelier's screens. Every other component reads the answer through
 * `useIsVitrine()` and `useInstanceContext()`, which read a CONTEXT the shell
 * provides - the pattern `admin-session.ts` has.
 *
 * A context and not a second `useQuery` per component, for one reason that is
 * about the tests as much as the screens: a component that asked the server
 * itself would make a request in each of the hundred suites that mount a card
 * or an editor without a shell, and every one of them would need to know about
 * a route that has nothing to do with it. Outside a provider the context holds
 * the atelier, which is what the screens drew before the vitrine existed -
 * nothing changes for them, and nothing is requested.
 *
 * ## The atelier is the fail-safe
 *
 * An answer that cannot be read (an old backend with no such route, a network
 * hiccup) is the atelier too: the screens then offer gestures that the server
 * of a vitrine would refuse with a 403 `vitrine-read-only` - a refusal, never
 * a write that slipped through. The lock is on the server (section 6); this is
 * only what the screen draws.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

/** The cache key of the one answer. */
export const INSTANCE_KEY = ['instance'] as const;

/** What a screen assumes when nobody told it better: the atelier, with no address to point at. */
export const ATELIER_INSTANCE: Instance = {
  role: 'atelier',
  code: 'local',
  origin: null,
  atelierOrigin: null,
  prodOrigin: null,
};

/** `GET /api/instance` - public, so it carries no credential, and parsed like every answer. */
export async function fetchInstance(): Promise<Instance> {
  const response = await fetch(`${API_URL}/api/instance`);
  if (!response.ok) throw new Error(`GET /api/instance a répondu ${response.status}`);
  return instanceSchema.parse(await response.json());
}

/**
 * The query. It never refetches: the role of a server cannot change under a
 * running process, and a deploy replaces the page with it.
 */
export function useInstance(): UseQueryResult<Instance> {
  return useQuery({
    queryKey: INSTANCE_KEY,
    queryFn: fetchInstance,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

const InstanceContext = createContext<Instance>(ATELIER_INSTANCE);

/** Mounted by the shell of the admin, around the screens. */
export const InstanceProvider = InstanceContext.Provider;

/** The instance the shell read - the atelier outside a shell. */
export function useInstanceContext(): Instance {
  return useContext(InstanceContext);
}

/** Is this the vitrine, where nothing of a model is written (section 6)? */
export function useIsVitrine(): boolean {
  return useContext(InstanceContext).role === 'vitrine';
}
